import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, GridApi } from 'ag-grid-community';
import { CommandeService } from '../../services/commande.service';
import { AuthService } from '../../services/auth.service';
import { Commande } from '../../models/commande.model';
import { User } from '../../models/user.model';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-commandes',
  standalone: true,
  imports: [AgGridAngular, FormsModule],
  templateUrl: './commandes.component.html',
  styleUrl: './commandes.component.scss'
})
export class CommandesComponent implements OnInit {
  commandes: Commande[] = [];
  isLoading = true;
  searchText = '';
  private gridApi!: GridApi;
  selectedCommandes: Commande[] = [];
  currentDate = new Date();
  currentUser: User | null = null;
  isMagasin = false;

  columnDefs: ColDef[] = [
    {
      headerName: 'N° Commande',
      field: 'NumCom',
      width: 150,
      cellStyle: { fontWeight: '600', color: '#1a202c' }
    },
    {
      headerName: 'Utilisateur',
      field: 'userName',
      width: 200,
      valueGetter: (params) => params.data?.userName || 'Utilisateur inconnu'
    },
    {
      headerName: 'Heure de récupération',
      field: 'heureRecuperation',
      width: 180
    },
    {
      headerName: 'Date de création',
      field: 'createdAt',
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => this.formatDate(params.value)
    },
    {
      headerName: 'Statut',
      field: 'statutOrdre',
      width: 150,
      cellRenderer: (params: any) => {
        const statut = params.data?.statutNom || 'Inconnu';
        const ordre = params.value;
        const colors: { [key: number]: { bg: string; text: string } } = {
          1: { bg: '#fef5e7', text: '#d68910' },  // En cours - orange
          2: { bg: '#e8f5e9', text: '#2e7d32' },  // Validée - vert
          3: { bg: '#f3e5f5', text: '#6a1b9a' },  // Récupérée - violet
          4: { bg: '#ffebee', text: '#c62828' },  // Non récupérée - rouge
          5: { bg: '#ffebee', text: '#c62828' }   // Annulée - rouge
        };
        const color = colors[ordre] || { bg: '#f7fafc', text: '#718096' };
        return `<span style="background: ${color.bg}; color: ${color.text}; padding: 6px 12px; border-radius: 12px; font-size: 12px; font-weight: 600;">${statut}</span>`;
      }
    },
    {
      headerName: 'Actions',
      field: 'id',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: () => {
        return `<button class="btn-reprint-grid" title="Réimprimer le ticket"><i class="pi pi-print"></i></button>`;
      },
      onCellClicked: (params: any) => {
        if (params.data?.id) {
          this.reprintCommande(params.data);
        }
      }
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };

  paginationPageSize = 20;
  paginationPageSizeSelector = [10, 20, 50, 100];
  theme: 'legacy' = 'legacy';

  rowSelection = {
    mode: 'multiRow' as const,
    checkboxes: true,
    headerCheckbox: true
  };

  constructor(
    private commandeService: CommandeService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.currentUser();
    // isMagasin = true pour magasin OU super_admin (accès complet pour super_admin)
    this.isMagasin = this.currentUser?.profils?.some(p => p.code === 'magasin' || p.code === 'super_admin') ?? false;
    this.loadCommandes();
  }

  loadCommandes(): void {
    // Le serveur filtre automatiquement selon le profil de l'utilisateur connecté
    this.commandeService.getAllCommandes().subscribe({
      next: (commandes) => {
        this.commandes = commandes.sort((a, b) => {
          return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
        });
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur chargement commandes:', error);
        this.isLoading = false;
      }
    });
  }

  // Fonction supprimée - on utilise directement statutNom du backend

  formatDate(date: string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  onSearch(): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', this.searchText);
    }
  }

  onSelectionChanged(event: any): void {
    this.selectedCommandes = event.api.getSelectedRows();
  }

  reprintCommande(commande: Commande): void {
    this.commandeService.printCommande(commande.id!).subscribe({
      next: () => {
        alert(`Ticket de la commande ${commande.NumCom} envoyé à l'impression`);
      },
      error: (error) => {
        console.error('Erreur impression:', error);
        alert(`Erreur lors de l'impression de la commande ${commande.NumCom}`);
      }
    });
  }

  printSelection(): void {
    let dataToExport: Commande[] = [];

    if (this.selectedCommandes.length > 0) {
      // Si sélection, exporter la sélection
      dataToExport = this.selectedCommandes;
    } else {
      // Récupérer toutes les lignes filtrées
      const allFilteredRows: Commande[] = [];
      this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
        if (node.data) {
          allFilteredRows.push(node.data);
        }
      });

      // Appliquer la pagination pour n'exporter que la page courante
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const pageSize = this.gridApi.paginationGetPageSize();
      const startRow = currentPage * pageSize;
      const endRow = startRow + pageSize;

      dataToExport = allFilteredRows.slice(startRow, endRow);
    }

    if (dataToExport.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    // Créer le PDF
    const doc = new jsPDF();

    // Titre
    doc.setFontSize(18);
    doc.setTextColor(26, 32, 44);
    doc.text('Liste des commandes', 14, 20);

    // Sous-titre
    doc.setFontSize(11);
    doc.setTextColor(74, 85, 104);
    const subtitle = this.selectedCommandes.length > 0
      ? `${this.selectedCommandes.length} commande(s) sélectionnée(s)`
      : `${dataToExport.length} commande(s) affichée(s)`;
    doc.text(subtitle, 14, 28);

    // Date
    doc.setFontSize(9);
    doc.setTextColor(113, 128, 150);
    doc.text(`Imprimé le ${this.formatDate(this.currentDate.toISOString())}`, 14, 35);

    // Préparer les données du tableau
    const tableData = dataToExport.map(cmd => [
      cmd.NumCom || '-',
      cmd.userName || 'Utilisateur inconnu',
      cmd.heureRecuperation || '-',
      this.formatDate(cmd.createdAt),
      cmd.statutNom || 'Inconnu'
    ]);

    // Générer le tableau
    autoTable(doc, {
      startY: 40,
      head: [['N° Commande', 'Utilisateur', 'Heure récupération', 'Date création', 'Statut']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [247, 250, 252],
        textColor: [26, 32, 44],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [226, 232, 240]
      },
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.5
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      }
    });

    // Sauvegarder le PDF
    const filename = `commandes_${new Date().getTime()}.pdf`;
    doc.save(filename);
  }
}
